import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Image,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { RadioChangeEvent } from 'antd';
import {
  EyeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  type ServiceProviderCertificationListParams,
  type ServiceProviderCertificationDetail,
  type ServiceProviderCertificationListItem,
  type ServiceProviderCertificationStatus,
  getServiceProviderCertificationDetail,
  listServiceProviderCertifications,
  reviewServiceProviderCertification,
} from '../../services/serviceProviderCertificationService';
import { normalizeFileUrl } from '../../services/fileService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';

const { Text } = Typography;

const certificationStatusOptions = [
  { label: '全部', value: 'all' as const },
  { label: '待审核', value: 'reviewing' as const },
  { label: '已通过', value: 'approved' as const },
  { label: '已驳回', value: 'rejected' as const },
];

const certificationStatusMap: Record<ServiceProviderCertificationStatus, { color: string; text: string }> = {
  reviewing: { color: 'processing', text: '待审核' },
  approved: { color: 'success', text: '已通过' },
  rejected: { color: 'error', text: '已驳回' },
};

const accountStatusMap: Record<string, { color: string; text: string }> = {
  active: { color: 'success', text: '正常' },
  inactive: { color: 'default', text: '禁用' },
};

const CertificationReview: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [items, setItems] = useState<ServiceProviderCertificationListItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<ServiceProviderCertificationDetail | null>(null);
  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');

  const [searchForm] = Form.useForm();
  const [reviewForm] = Form.useForm();
  const tableWrapRef = useHorizontalTableWheel();

  const reviewStatus = Form.useWatch('certificationStatus', reviewForm);

  const loadData = async (
    page = pagination.page,
    pageSize = pagination.pageSize,
    overrides: Partial<ServiceProviderCertificationListParams> = {},
  ) => {
    setLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const res = await listServiceProviderCertifications({
        page,
        pageSize,
        keyword: values.keyword || undefined,
        serviceRegion: values.serviceRegion || undefined,
        certificationStatus: values.certificationStatus || 'reviewing',
        ...overrides,
      });
      setItems(res.list || []);
      setPagination({
        page,
        pageSize,
        total: res.total || 0,
      });
    } catch {
      message.error('加载认证审核列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const res = await listServiceProviderCertifications({
          page: 1,
          pageSize: 10,
          certificationStatus: 'reviewing',
        });
        setItems(res.list || []);
        setPagination({
          page: 1,
          pageSize: 10,
          total: res.total || 0,
        });
      } catch {
        message.error('加载认证审核列表失败');
      } finally {
        setLoading(false);
      }
    };

    void initialize();
  }, []);

  const handleSearch = () => {
    loadData(1, pagination.pageSize);
  };

  const handleCertificationStatusChange = (event: RadioChangeEvent) => {
    const certificationStatus = event.target.value || 'reviewing';
    searchForm.setFieldsValue({ certificationStatus });
    loadData(1, pagination.pageSize, { certificationStatus });
  };

  const handleReset = () => {
    searchForm.setFieldsValue({
      keyword: undefined,
      serviceRegion: undefined,
      certificationStatus: 'reviewing',
    });
    loadData(1, pagination.pageSize, { keyword: undefined, serviceRegion: undefined, certificationStatus: 'reviewing' });
  };

  const loadDetail = async (id: string, openDrawer = true) => {
    setDetailLoading(true);
    try {
      const detail = await getServiceProviderCertificationDetail(id);
      setCurrentDetail(detail);
      if (openDrawer) {
        setDetailOpen(true);
      }
      return detail;
    } catch {
      message.error('加载认证详情失败');
      return null;
    } finally {
      setDetailLoading(false);
    }
  };

  const handleView = async (id: string) => {
    await loadDetail(id, true);
  };

  const handleOpenReview = async (id?: string) => {
    const targetId = id || currentDetail?.id;
    if (!targetId) {
      return;
    }

    let detail = currentDetail;
    if (!detail || detail.id !== targetId) {
      detail = await loadDetail(targetId, false);
    }
    if (!detail) {
      return;
    }

    reviewForm.setFieldsValue({
      certificationStatus: 'approved',
      certificationRemark: detail.certificationStatus === 'rejected' ? detail.certificationRemark : '',
    });
    setReviewOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!currentDetail) {
      return;
    }

    try {
      const values = await reviewForm.validateFields();
      setReviewSubmitting(true);
      const detail = await reviewServiceProviderCertification(currentDetail.id, {
        certificationStatus: values.certificationStatus,
        certificationRemark: values.certificationRemark?.trim() || '',
      });
      setCurrentDetail(detail);
      setReviewOpen(false);
      message.success(values.certificationStatus === 'approved' ? '审核通过成功' : '驳回成功');
      loadData();
    } catch {
      // ignore
    } finally {
      setReviewSubmitting(false);
    }
  };

  const renderCertificationStatus = (status: ServiceProviderCertificationStatus) => {
    const meta = certificationStatusMap[status] || { color: 'default', text: status };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const renderAccountStatus = (status: string) => {
    const meta = accountStatusMap[status] || { color: 'default', text: status || '-' };
    return <Tag color={meta.color}>{meta.text}</Tag>;
  };

  const renderProjects = (projects: string[], fallbackText?: string) => {
    if (projects?.length) {
      return (
        <Space size={[0, 6]} wrap>
          {projects.map((item) => (
            <Tag key={item}>{item}</Tag>
          ))}
        </Space>
      );
    }
    return <Text type="secondary">{fallbackText || '-'}</Text>;
  };

  const renderImageCard = (title: string, url?: string) => (
    <div style={{ width: 180 }}>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>{title}</div>
      {url ? (
        <Image
          src={normalizeFileUrl(url)}
          width={180}
          height={120}
          style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
        />
      ) : (
        <div
          style={{
            height: 120,
            border: '1px dashed #d9d9d9',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
          }}
        >
          暂无图片
        </div>
      )}
    </div>
  );

  const openVideoPreview = (url?: string) => {
    const normalized = normalizeFileUrl(url);
    if (!normalized) {
      return;
    }
    setVideoPreviewUrl(normalized);
    setVideoPreviewOpen(true);
  };

  const renderVideoCard = (title: string, url?: string) => {
    const normalizedUrl = normalizeFileUrl(url);
    return (
      <div style={{ width: 180 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{title}</div>
        {normalizedUrl ? (
          <div
            onClick={() => openVideoPreview(url)}
            style={{
              width: 180,
              height: 120,
              borderRadius: 8,
              border: '1px solid #f0f0f0',
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer',
              background: '#000',
            }}
          >
            <video
              src={normalizedUrl}
              muted
              preload="metadata"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.24)',
                color: '#fff',
                fontSize: 28,
              }}
            >
              <PlayCircleOutlined />
            </div>
          </div>
        ) : (
          <div
            style={{
              height: 120,
              border: '1px dashed #d9d9d9',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
            }}
          >
            暂无视频
          </div>
        )}
      </div>
    );
  };

  const columns: ColumnsType<ServiceProviderCertificationListItem> = [
    {
      title: '服务人员',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (_, record) => (
        <Space>
          <Avatar src={record.avatar ? normalizeFileUrl(record.avatar) : undefined}>
            {record.name?.slice(0, 1)}
          </Avatar>
          <div>
            <div>{record.name || '-'}</div>
            <Text type="secondary">{record.username || '-'}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (value: string) => value || '-',
    },
    {
      title: '服务区域',
      dataIndex: 'serviceRegion',
      key: 'serviceRegion',
      width: 180,
      render: (value: string) => value || '-',
    },
    {
      title: '服务项目',
      dataIndex: 'serviceProjects',
      key: 'serviceProjects',
      width: 260,
      render: (value: string[], record) => renderProjects(value, record.serviceProjectsText),
    },
    {
      title: '认证状态',
      dataIndex: 'certificationStatus',
      key: 'certificationStatus',
      width: 100,
      render: renderCertificationStatus,
    },
    {
      title: '账号状态',
      dataIndex: 'accountStatus',
      key: 'accountStatus',
      width: 100,
      render: renderAccountStatus,
    },
    {
      title: '提交时间',
      dataIndex: 'certificationSubmittedAt',
      key: 'certificationSubmittedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '审核时间',
      dataIndex: 'certificationReviewedAt',
      key: 'certificationReviewedAt',
      width: 170,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record.id)}>
            详情
          </Button>
          {record.certificationStatus === 'reviewing' && (
            <Button size="small" type="primary" icon={<SafetyOutlined />} onClick={() => handleOpenReview(record.id)}>
              审核
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={(
          <>
            <SafetyOutlined /> 认证审核
          </>
        )}
        extra={(
          <Button icon={<ReloadOutlined />} onClick={() => loadData()}>
            刷新
          </Button>
        )}
      >
        <Form
          form={searchForm}
          layout="inline"
          initialValues={{ certificationStatus: 'reviewing' }}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword" label="关键词">
            <Input allowClear placeholder="姓名 / 账号 / 手机号 / 微信昵称" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item name="serviceRegion" label="服务区域">
            <Input allowClear placeholder="省 / 市 / 区" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="certificationStatus" label="认证状态">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={certificationStatusOptions}
              onChange={handleCertificationStatusChange}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        <div ref={tableWrapRef}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={items}
            scroll={{ x: 1500 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => loadData(page, pageSize),
            }}
          />
        </div>
      </Card>

      <Drawer
        title="认证详情"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        size={920}
      >
        {!currentDetail ? null : (
          <Space orientation="vertical" size={24} style={{ width: '100%' }}>
            <Card loading={detailLoading} size="small" title="基础信息">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="头像">
                  <Avatar size={56} src={currentDetail.avatar ? normalizeFileUrl(currentDetail.avatar) : undefined}>
                    {currentDetail.name?.slice(0, 1)}
                  </Avatar>
                </Descriptions.Item>
                <Descriptions.Item label="姓名">{currentDetail.name || '-'}</Descriptions.Item>
                <Descriptions.Item label="登录账号">{currentDetail.username || '-'}</Descriptions.Item>
                <Descriptions.Item label="手机号">{currentDetail.phone || '-'}</Descriptions.Item>
                <Descriptions.Item label="微信昵称">{currentDetail.wechatNickname || '-'}</Descriptions.Item>
                <Descriptions.Item label="服务等级">{currentDetail.levelName || '-'}</Descriptions.Item>
                <Descriptions.Item label="认证状态">
                  {renderCertificationStatus(currentDetail.certificationStatus)}
                </Descriptions.Item>
                <Descriptions.Item label="账号状态">
                  {renderAccountStatus(currentDetail.accountStatus)}
                </Descriptions.Item>
                <Descriptions.Item label="提交时间">{currentDetail.certificationSubmittedAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="审核时间">{currentDetail.certificationReviewedAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="服务区域" span={2}>
                  {currentDetail.serviceRegion || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="详细地址" span={2}>
                  {currentDetail.serviceAddressDetail || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="服务项目" span={2}>
                  {renderProjects(currentDetail.serviceProjects, currentDetail.serviceProjectsText)}
                </Descriptions.Item>
                <Descriptions.Item label="服务经验" span={2}>
                  {currentDetail.serviceExperience || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="审核备注" span={2}>
                  {currentDetail.certificationRemark || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card loading={detailLoading} size="small" title="认证材料">
              <Space size={16} wrap>
                {renderImageCard('身份证正面', currentDetail.idCardFrontUrl)}
                {renderImageCard('身份证反面', currentDetail.idCardBackUrl)}
                {renderVideoCard('视频认证', currentDetail.extraMaterialUrl)}
              </Space>
            </Card>

            <Card loading={detailLoading} size="small" title="附加信息">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="App 类型">{currentDetail.appType || '-'}</Descriptions.Item>
                <Descriptions.Item label="最后登录时间">{currentDetail.lastLoginAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="最后登录 IP">{currentDetail.lastLoginIp || '-'}</Descriptions.Item>
                <Descriptions.Item label="通知开关">{currentDetail.notifyEnabled ? '开启' : '关闭'}</Descriptions.Item>
                <Descriptions.Item label="接单模式">{currentDetail.orderMode ? '自动' : '手动'}</Descriptions.Item>
                <Descriptions.Item label="创建时间">{currentDetail.createdAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="更新时间">{currentDetail.updatedAt || '-'}</Descriptions.Item>
                <Descriptions.Item label="经纬度">
                  {currentDetail.serviceLatitude || currentDetail.serviceLongitude
                    ? `${currentDetail.serviceLatitude}, ${currentDetail.serviceLongitude}`
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {currentDetail.certificationStatus === 'reviewing' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="primary" icon={<SafetyOutlined />} onClick={() => handleOpenReview()}>
                  审核
                </Button>
              </div>
            )}
          </Space>
        )}
      </Drawer>

      <Modal
        title="认证审核"
        open={reviewOpen}
        onCancel={() => setReviewOpen(false)}
        onOk={handleSubmitReview}
        confirmLoading={reviewSubmitting}
        width={720}
        centered
        destroyOnHidden
        mask={{ closable: false }}
        okText="提交审核"
        cancelText="取消"
        zIndex={1600}
      >
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card size="small" title="审核对象">
            <Descriptions column={2} size="small">
              <Descriptions.Item label="服务人员">{currentDetail?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="手机号">{currentDetail?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="服务区域">{currentDetail?.serviceRegion || '-'}</Descriptions.Item>
              <Descriptions.Item label="提交时间">{currentDetail?.certificationSubmittedAt || '-'}</Descriptions.Item>
              <Descriptions.Item label="当前状态">
                {currentDetail ? renderCertificationStatus(currentDetail.certificationStatus) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="账号状态">
                {currentDetail ? renderAccountStatus(currentDetail.accountStatus) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="服务项目" span={2}>
                {renderProjects(currentDetail?.serviceProjects || [], currentDetail?.serviceProjectsText)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card size="small" title="认证材料">
            <Space size={16} wrap>
              {renderImageCard('身份证正面', currentDetail?.idCardFrontUrl)}
              {renderImageCard('身份证反面', currentDetail?.idCardBackUrl)}
              {renderVideoCard('视频认证', currentDetail?.extraMaterialUrl)}
            </Space>
          </Card>

          <Form
            form={reviewForm}
            layout="vertical"
            initialValues={{ certificationStatus: 'approved', certificationRemark: '' }}
          >
            <Form.Item
              name="certificationStatus"
              label="审核结果"
              rules={[{ required: true, message: '请选择审核结果' }]}
            >
              <Radio.Group>
                <Radio value="approved">审核通过</Radio>
                <Radio value="rejected">审核驳回</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="certificationRemark"
              label="审核备注"
              extra={reviewStatus === 'rejected' ? '驳回时必须填写具体原因，服务人员会看到该备注。' : '通过时可选填备注。'}
              rules={[
                {
                  validator: async (_, value) => {
                    if (reviewStatus === 'rejected' && !String(value || '').trim()) {
                      throw new Error('驳回时请填写审核备注');
                    }
                  },
                },
              ]}
            >
              <Input.TextArea
                rows={5}
                maxLength={500}
                showCount
                placeholder={reviewStatus === 'rejected' ? '请填写驳回原因，例如证件信息不清晰、服务信息不完整等' : '可选，填写通过备注'}
              />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Modal
        title="视频认证预览"
        open={videoPreviewOpen}
        footer={null}
        onCancel={() => {
          setVideoPreviewOpen(false);
          setVideoPreviewUrl('');
        }}
        width={720}
        destroyOnHidden
      >
        {videoPreviewUrl ? (
          <video
            src={videoPreviewUrl}
            controls
            preload="metadata"
            style={{ width: '100%', maxHeight: '70vh', borderRadius: 8, background: '#000' }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default CertificationReview;
